import 'isomorphic-fetch';

import baseFetch, { createParamString } from 'common/baseFetch';
import config from 'config';
import type { Camera } from 'types/camera';
import type { BatchSpotReportViews, PopularSpotReportViews } from 'types/spot';
import type { Units } from 'types/units';
import addClientIpHeader from 'utils/addClientIpHeader';
import addGeoCountryCodeHeader from 'utils/addGeoCountryCodeHeader';

/**
 * Merges `internalCameras` into `cameras` and tags each with `isInternalCam`.
 */
export const mergeInternalCameras = <T extends { cameras?: Camera[]; internalCameras?: Camera[] }>(
  spot: T,
): T => {
  if (!spot) return spot;
  const internalCameras = spot.internalCameras ?? [];
  if (internalCameras.length === 0) return spot;

  const internalCamIds = new Set(internalCameras.map((c) => c._id));
  const existingIds = new Set((spot.cameras ?? []).map((c) => c._id));

  const taggedExisting = (spot.cameras ?? []).map((cam) => ({
    ...cam,
    isInternalCam: internalCamIds.has(cam._id),
  }));

  const newInternalCams = internalCameras
    .filter((cam) => !existingIds.has(cam._id))
    .map((cam) => ({ ...cam, isInternalCam: true }));

  return {
    ...spot,
    cameras: [...taggedExisting, ...newInternalCams],
  };
};

export const fetchReport = async ({
  clientIp = null,
  cookies,
  countryCode = null,
  includeInternalCams = false,
  spotId,
}: {
  clientIp?: string | null;
  cookies?: Record<string, string>;
  countryCode?: string | null;
  includeInternalCams?: boolean;
  spotId: string;
}) => {
  const clientIpHeader = addClientIpHeader({ clientIp });
  const countryCodeHeader = addGeoCountryCodeHeader({ countryCode });

  const queryParams: Record<string, any> = {
    spotId,
    ...(includeInternalCams && { includeInternalCams: true }),
  };

  const response = await baseFetch(`/kbyg/spots/reports?${createParamString(queryParams)}`, {
    cookies,
    headers: {
      ...clientIpHeader,
      ...countryCodeHeader,
    } as HeadersInit,
  });
  return { ...response, spot: mergeInternalCameras(response.spot) };
};

export const fetchNearbySpots = async (
  spotId: string,
  cookies: Record<string, string>,
  clientIp = null,
  countryCode = null,
  includeInternalCams = false,
) => {
  const clientIpHeader = addClientIpHeader({ clientIp });
  const countryCodeHeader = addGeoCountryCodeHeader({ countryCode });

  const queryParams: Record<string, any> = {
    spotId,
    ...(includeInternalCams && { includeInternalCams: true }),
  };

  const response = await baseFetch(`/kbyg/spots/nearby?${createParamString(queryParams)}`, {
    cookies,
    headers: {
      ...clientIpHeader,
      ...countryCodeHeader,
    } as HeadersInit,
  });
  return {
    ...response,
    data: {
      ...response.data,
      spots: response.data?.spots?.map(mergeInternalCameras),
    },
  };
};

export const fetchSpotDetails = (spotId: string, cookies: Record<string, string>) =>
  baseFetch(`/kbyg/spots/details?spotId=${spotId}`, { cookies });

export const fetchBuoys = async (legacySpotId: string, units: Units) => {
  const url = `${config.legacyApi}/mobile/nearby/${legacySpotId}?resources=buoy&unit=${units}&buoys=6`;
  const response = await fetch(url);
  const body = await response.json();
  if (response.status > 200) throw body;
  return body;
};

export const fetchBatchSubregions = (subregionIds: Array<string>) =>
  baseFetch(`/subregions/batch?${createParamString({ subregionIds })}`);

export const fetchNearestSpot = (lat: number, lon: number, includeInternalCams = false) => {
  const queryParams: Record<string, any> = {
    lat,
    lon,
    ...(includeInternalCams && { includeInternalCams: true }),
  };
  return baseFetch(`/kbyg/mapview/spot?${createParamString(queryParams)}`);
};

interface FetchSpotReportViewsData {
  units:
    | {
        swellHeight?: string;
        temperature?: string;
        tideHeight?: string;
        waveHeight?: string;
        windSpeed?: string;
        surfHeight?: string;
      }
    | undefined;
  noCache?: string;
}

interface FetchSpotReportViewsOptions {
  method: string;
  headers: {
    Accept: string;
    'Content-Type': string;
    'Cache-Control'?: string;
  };
}

export const fetchSpotReportViews = async (
  spotIds: Array<string>,
  data: FetchSpotReportViewsData = {
    units: undefined,
    noCache: 'false',
  },
  includeInternalCams = false,
): Promise<BatchSpotReportViews> => {
  let cacheEnabled = !!(
    data.units?.surfHeight &&
    data.units?.swellHeight &&
    data.units?.temperature &&
    data.units?.tideHeight &&
    data.units?.windSpeed
  );

  const options: FetchSpotReportViewsOptions = {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  };

  if (data?.noCache?.toLowerCase() === 'true') {
    options.headers['Cache-Control'] = 'no-cache';
    cacheEnabled = false;
  }

  let url = `/kbyg/spots/batch?${createParamString({
    cacheEnabled,
    ...(includeInternalCams && { includeInternalCams: true }),
    'units[swellHeight]': data.units?.swellHeight ?? null,
    'units[temperature]': data.units?.temperature ?? null,
    'units[tideHeight]': data.units?.tideHeight ?? null,
    'units[waveHeight]': data.units?.surfHeight ?? null,
    'units[windSpeed]': data.units?.windSpeed ?? null,
  })}`;

  // Only add spotIds query param if there are any passed in.
  if (spotIds.length > 0) {
    // Sort the spot ids alphanumerically so they match the server response.
    // This ensures caching.
    const sortedSpotIds = spotIds.sort((a, b) =>
      a.localeCompare(b, undefined, {
        numeric: true,
        sensitivity: 'base',
      }),
    );
    url = `${url}&${createParamString({ spotIds: sortedSpotIds })}`;
  }

  return baseFetch(url, options);
};

export const fetchPopularSpots = async (
  geoCountryIso?: string,
  includeInternalCams = false,
): Promise<PopularSpotReportViews> => {
  const url = `/kbyg/spots/popular?geoCountryIso=${geoCountryIso}${includeInternalCams ? '&includeInternalCams=true' : ''}`;
  return baseFetch(url);
};
